import { AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { useIntl } from 'react-intl'

import useClickOutside from 'shared/hooks/useClickOutside'
import { PageWorkflowStatus } from 'shared/types/global'
import PageStatus from 'shared/ui/PageStatus'
import {
  PAGE_STATUS_LABEL_IDS,
  SELECTABLE_PAGE_STATUSES
} from 'shared/ui/PageStatus/constants'

import {
  BallButton,
  BallInner,
  Container,
  Dropdown,
  Trigger
} from './Styles'

type PageStatusSelectProps = {
  status: PageWorkflowStatus
  onChange: (status: PageWorkflowStatus) => void
  onOpenChange?: (isOpen: boolean) => void
  size?: number
  disabled?: boolean
}

// Orchestrates the staggered pop of the individual balls.
const listVariants = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.1,
      staggerChildren: 0.03,
      staggerDirection: 1
    }
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.12,
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  }
}

const ballVariants = {
  hidden: { opacity: 0, scale: 0.2, y: 6 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 520, damping: 26 }
  }
}

const PageStatusSelect: React.FC<PageStatusSelectProps> = ({
  status,
  onChange,
  onOpenChange,
  size = 16,
  disabled = false
}) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(false)

  const setOpen = (next: boolean) => {
    setIsOpen(next)
    onOpenChange?.(next)
  }

  const ref = useClickOutside(() => setOpen(false))

  const handleSelect = (
    event: React.MouseEvent,
    nextStatus: PageWorkflowStatus
  ) => {
    event.stopPropagation()
    setOpen(false)

    if (nextStatus !== status) {
      onChange(nextStatus)
    }
  }

  const handleTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation()

    if (disabled) {
      return
    }

    setOpen(!isOpen)
  }

  const options = SELECTABLE_PAGE_STATUSES.filter(
    option => option !== status
  )

  return (
    <Container
      ref={ref}
      onClick={event => event.stopPropagation()}
      onPointerDown={event => event.stopPropagation()}
    >
      <Trigger
        type="button"
        disabled={disabled}
        onClick={handleTriggerClick}
      >
        <PageStatus status={status} size={size} />
      </Trigger>

      <AnimatePresence>
        {isOpen && (
          <Dropdown
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={listVariants}
          >
            {options.map(option => (
              <BallButton
                key={option}
                type="button"
                title={formatMessage({
                  id: PAGE_STATUS_LABEL_IDS[option]
                })}
                variants={ballVariants}
                onClick={event => handleSelect(event, option)}
              >
                <BallInner>
                  <PageStatus status={option} size={size} />
                </BallInner>
              </BallButton>
            ))}
          </Dropdown>
        )}
      </AnimatePresence>
    </Container>
  )
}

export default PageStatusSelect
